/**
 * The schema of a date-shaped query parameter, shared by the routes (backend) and the screens that declare the same
 * filters in their contract.
 *
 * Two rules, and neither is spare: an ISO form with a REAL calendar (`z.iso.date()` refuses `2026-02-30`) and a year
 * range (`z.iso.date()` accepts `0000-01-01`, which PostgreSQL refuses: it was 24 of the 80 answers 500 measured on
 * 2026-09-21). It is NOT validated by parsing with `Date`: `new Date('2026-02-30')` is 2 March, the limit of a report
 * would move silently.
 *
 *   2026-01-15                        a date alone
 *   2026-01-15T10:30:00Z              with a time, in UTC
 *   2026-01-15T10:30:00.000+02:00     with a time and an offset
 */

import { z } from 'zod/v4'

/** First and last year the four-digit ISO format can express. */
export const ISO_DATE_YEAR_MIN = 1
export const ISO_DATE_YEAR_MAX = 9999

/**
 * The `refine` reads the first four characters on purpose: by then the string already has a valid ISO form, so those
 * four are the year; converting it to a `Date` would bring back the tolerant parsing this schema avoids.
 */
export const isoDateQueryParamSchema = z
  .union([z.iso.date(), z.iso.datetime({ offset: true })])
  .refine((value) => {
    const year = Number(value.slice(0, 4))
    return year >= ISO_DATE_YEAR_MIN && year <= ISO_DATE_YEAR_MAX
  }, `the year must be between ${ISO_DATE_YEAR_MIN} and ${ISO_DATE_YEAR_MAX}`)
